import { useCallback } from 'react';
import { BudgetStatusEnum, getBudgetStatusFilter } from '../../../../../enums/budgetStatus';
import { BudgetRecipientEnum, IBudgetFormData } from '../../../createAndEdit/types';
import { IBudgetsListResponse, IBudgetListItem } from '../../../../../api/fetchBudget';

interface IUseFactoryListJourneyParams {
  budgetsListResponse: IBudgetsListResponse | null | undefined;
  userProfile: BudgetRecipientEnum | undefined;
  onNavigateToView: (budgetId: string, data: IBudgetFormData) => void;
  onNavigateToEdit: (budgetId: string, data: IBudgetFormData) => void;
  loadBudgetData: (
    budgetId: string,
    versionNumber?: number,
  ) => Promise<IBudgetFormData | null>;
}

export const useFactoryListJourney = (params: IUseFactoryListJourneyParams) => {
  const {
    budgetsListResponse,
    userProfile,
    onNavigateToView,
    onNavigateToEdit,
    loadBudgetData,
  } = params;

  const handleViewOrEditBudget = useCallback(
    async (budgetId: string) => {
      const budgetItem = budgetsListResponse?.budgets.find((item: IBudgetListItem) => item.uuid === budgetId);
      const budgetStatus = budgetItem?.status as BudgetStatusEnum | string;
      const budgetRecipient = budgetItem?.recipient as BudgetRecipientEnum | undefined;

      if (!userProfile || !budgetRecipient) {
        const budgetData = await loadBudgetData(budgetId);
        if (budgetData) {
          onNavigateToView(budgetId, budgetData);
        }
        return;
      }

      const statusEnum =
        typeof budgetStatus === 'string'
          ? getBudgetStatusFilter(budgetStatus)
          : (budgetStatus as BudgetStatusEnum);

      if (
        statusEnum === BudgetStatusEnum.PENDING ||
        statusEnum === BudgetStatusEnum.APPROVED ||
        budgetStatus === 'Pendente' ||
        budgetStatus === 'Aprovado'
      ) {
        const budgetData = await loadBudgetData(budgetId);
        if (budgetData) {
          onNavigateToView(budgetId, budgetData);
        }
        return;
      }

      const isRecipientDifferentFromUser = budgetRecipient !== userProfile;
      if (isRecipientDifferentFromUser) {
        const budgetData = await loadBudgetData(budgetId);
        if (budgetData) {
          onNavigateToView(budgetId, budgetData);
        }
      } else {
        const versionNumber = budgetItem?.lastVersionNumber || 1;
        const editData = await loadBudgetData(budgetId, versionNumber);
        if (editData) {
          editData.versionNumber = versionNumber;
          if (budgetItem) {
            editData.status = budgetItem.status as BudgetStatusEnum;
            if (budgetItem.recipient) {
              editData.recipient = budgetItem.recipient;
            }
          }
          onNavigateToEdit(budgetId, editData);
        }
      }
    },
    [budgetsListResponse, userProfile, onNavigateToView, onNavigateToEdit, loadBudgetData],
  );

  return {
    handleViewOrEditBudget,
  };
};

